/**
 * Blinkit-theme cart (``/cart``) — where this app's customers review and place the order. All state and actions
 * come from useCartController (the classic cart's own logic, including the payment flow); this file is layout.
 *
 *   header · delivery-time banner · items (− qty +) · offers & coupons (+ coins) · delivery address ·
 *   payment method (+ wallet) · price details · fixed "Place order" bar with the amount actually charged
 */
import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Clock, Minus, Package, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { useShopEta } from "../../../lib/useShopEta";
import { coinCapSubtotal, coinRedeemCap, coinRedeemMin } from "../../../lib/coinRedemption";
import { fmtInr } from "../../../lib/priceBreakdown";
import AddressPickerModal from "../../../components/AddressPickerModal";
import { BkCard, BkEmptyState, BkErrorState, BkPageHeader } from "../../../components/blinkit/BkUI";
import CouponPickerModal from "../_partials/CouponPickerModal";
import { AddressPickerDrawer, CoinsRedeemBlock } from "../_partials/CartPickers";
import { useCartController } from "../controllers/useCartController";
import DeliveryOptions from "../../../components/lse/DeliveryOptions";
import OfflineCartNotice from "../../../components/lse/OfflineCartNotice";
import { deliveryBlocking } from "../../../lib/lse";
import { cartShopLabel } from "../../../lib/multiShop";
import { ShopBreakdown, ShopDivider } from "../../../components/MultiShop";
import {
    BkAddressSection, BkCouponSection, BkNotice, BkPaymentOverlay, BkPaymentSection, BkPlaceOrderBar, BkPriceDetails, PAY_ICONS,
} from "./BkCheckoutParts";

export function BkCartSkeleton() {
    return (
        <div className="px-4 pt-4 space-y-3" aria-hidden="true" data-testid="bk-cart-loading">
            {[88, 140, 72, 160].map((h, i) => <div key={i} className="rounded-2xl bg-card border border-border/80 animate-pulse" style={{ height: h }} />)}
        </div>
    );
}

function CartLine({ it, onChange }) {
    const each = Number(it.selling_price || 0);
    const mrp = Number(it.mrp || 0);
    return (
        <li className="flex items-center gap-3 py-3 first:pt-0 last:pb-0" data-testid={`cart-item-${it.product_id}`}>
            <Link to={`/product/${it.product_id}`} className="w-14 h-14 shrink-0 rounded-xl bg-muted/60 border border-border/60 overflow-hidden flex items-center justify-center">
                {it.image_url ? <img src={it.image_url} alt="" className="w-full h-full object-contain" loading="lazy" /> : <Package size={20} className="text-muted-foreground/60" />}
            </Link>
            <div className="min-w-0 flex-1">
                <p className="text-[13.5px] font-semibold leading-snug line-clamp-2">{it.name}</p>
                <p className="mt-0.5 text-[12px] text-muted-foreground tabular-nums">
                    {fmtInr(each)} each
                    {mrp > each && <span className="ml-1.5 line-through" data-testid={`cart-mrp-${it.product_id}`}>{fmtInr(mrp)}</span>}
                </p>
            </div>
            <div className="shrink-0 flex flex-col items-end gap-1.5">
                <div className="h-9 inline-flex items-center rounded-lg bg-primary text-primary-foreground overflow-hidden" data-testid={`cart-stepper-${it.product_id}`}>
                    <button type="button" data-testid={`cart-dec-${it.product_id}`} onClick={() => onChange(it, -1)} aria-label={it.quantity === 1 ? `Remove ${it.name}` : "Decrease quantity"}
                            className="h-full w-9 inline-flex items-center justify-center active:bg-black/10">
                        {it.quantity === 1 ? <Trash2 size={14} /> : <Minus size={14} strokeWidth={3} />}
                    </button>
                    <span className="min-w-[22px] text-center text-[13px] font-extrabold tabular-nums" data-testid={`cart-qty-${it.product_id}`}>{it.quantity}</span>
                    <button type="button" data-testid={`cart-inc-${it.product_id}`} onClick={() => onChange(it, 1)} aria-label="Increase quantity"
                            className="h-full w-9 inline-flex items-center justify-center active:bg-black/10">
                        <Plus size={14} strokeWidth={3} />
                    </button>
                </div>
                <span className="text-[13.5px] font-extrabold tabular-nums" data-testid={`cart-line-total-${it.product_id}`}>{fmtInr(it.line_total)}</span>
            </div>
        </li>
    );
}

export default function BkCartView() {
    const nav = useNavigate();
    const c = useCartController({
        waitForAuth: true,
        onPlaced: (orderId) => nav(`/orders/${orderId}`, { replace: true, state: { justPlaced: true } }),
    });
    const {
        cart, code, setCode, coupon, busy, addrs, picker, setPicker, showAddr, setShowAddr, selectedAddrId,
        setSelectedAddrId, payMethods, payment, setPayment, placing, payPhase, lastError, setLastError, showCouponPicker,
        setShowCouponPicker, availCoupons, couponPickerBusy, coinsBalance, coinsConfig, coinsToRedeem, setCoinsToRedeem,
        useWallet, setUseWallet, walletBalance, loadAddrs, loadError, retryLoad, selectedAddr, change, apply, removeCoupon, openCouponPicker,
        applyCouponFromPicker, placeOrder,
    } = c;

    const totals = useMemo(() => cart?.totals || {}, [cart]);
    const items = cart?.items || [];
    const eta = useShopEta(cart?.restaurant_id, selectedAddr);

    if (!cart) {
        return (
            <div className="pb-40" data-testid="bk-cart-page">
                <BkPageHeader title="Cart" back="/" />
                {loadError
                    ? <BkErrorState title="Couldn't load your cart" body="Check your connection and try again — your items are saved." onRetry={retryLoad} testid="bk-cart-error" />
                    : <BkCartSkeleton />}
            </div>
        );
    }
    if (items.length === 0) {
        return (
            <div className="pb-40" data-testid="bk-cart-page">
                <BkPageHeader title="Cart" back="/" />
                <BkEmptyState icon={ShoppingBag} testid="bk-cart-empty" title="Your cart is empty"
                              body="Add products from any store and they'll show up here."
                              action={<Link to="/" data-testid="browse-restaurants" className="inline-flex h-11 items-center px-6 rounded-xl bg-primary text-primary-foreground text-[14px] font-bold">Start shopping</Link>} />
            </div>
        );
    }

    const grand = Number(totals.grand_total || 0);
    const count = items.reduce((s, i) => s + i.quantity, 0);
    const walletApplied = useWallet ? Math.min(walletBalance, grand) : 0;
    const walletFullyCovers = useWallet && walletBalance >= grand;
    const toPay = Math.max(0, Math.round((grand - walletApplied) * 100) / 100);
    const methods = [
        payMethods.cod && { id: "COD", label: "Cash on delivery", sub: "Pay with cash when your order arrives", icon: PAY_ICONS.COD, testid: "pay-cod" },
        payMethods.online && { id: "ONLINE", label: "Pay online", sub: payMethods.razorpay_mock ? "UPI, cards, netbanking (test mode)" : "UPI, cards, netbanking — secured by Razorpay", icon: PAY_ICONS.ONLINE, testid: "pay-online" },
    ].filter(Boolean);
    const needsMethod = !walletFullyCovers;
    const outOfZone = !!totals.delivery_out_of_zone;
    // Location Service Engine tenants only: a delivery option still to pick / an area rule the cart breaks.
    const lseBlock = deliveryBlocking(c.lse);
    const disabled = placing || !selectedAddr || (needsMethod && !payment) || (needsMethod && methods.length === 0) || outOfZone
        || !!lseBlock || c.offline;
    const buttonLabel = outOfZone ? "Can't deliver to this address"
        : c.offline ? "You're offline"
        : lseBlock ? lseBlock
        : !selectedAddr ? "Add an address to continue"
            : walletFullyCovers ? "Pay with wallet"
                : payment === "ONLINE" ? "Pay & place order" : "Place order";

    return (
        <div className="pb-48 lg:pb-16 min-w-0" data-testid="bk-cart-page">
            <BkPageHeader title="Cart" subtitle={`${cartShopLabel(cart)} · ${count} item${count === 1 ? "" : "s"}`} back="/" />

            {/* Location Service Engine offline cart: first thing on screen, since the page is view-only until reconnected. */}
            {c.offline && <div className="px-4 pt-4"><OfflineCartNotice savedAt={cart?._offline_saved_at} /></div>}
            <div className="px-4 pt-4 lg:grid lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] lg:gap-5 lg:items-start">
                <div className="space-y-3 min-w-0">
                    {eta && (
                        <div className="flex items-center gap-3 rounded-2xl bg-primary text-primary-foreground px-4 py-3" data-testid="bk-cart-eta">
                            <span className="w-10 h-10 shrink-0 rounded-xl bg-white/15 flex items-center justify-center"><Clock size={20} /></span>
                            <div className="min-w-0">
                                <p className="text-[15px] font-extrabold leading-tight">Delivery in {eta}</p>
                                <p className="text-[12px] opacity-85 truncate">From {cartShopLabel(cart)}</p>
                            </div>
                        </div>
                    )}

                    <BkCard testid="bk-cart-items">
                        <ul className="divide-y divide-border">
                            {items.map((it, idx) => [
                                // Multi-shop checkout only (renders nothing otherwise): a heading before each shop's items.
                                <ShopDivider key={`shop-${it.product_id}-${it.variant_id || ""}`} as="li" cart={cart} items={items} index={idx} />,
                                <CartLine key={`${it.product_id}-${it.variant_id || ""}`} it={it} onChange={change} />,
                            ])}
                        </ul>
                        {cart.restaurant_id && (
                            <Link to={`/restaurant/${cart.restaurant_id}`} data-testid="bk-cart-add-more"
                                  className="mt-3 flex items-center justify-center gap-1.5 h-10 rounded-xl border border-dashed border-primary/40 text-primary text-[13px] font-bold hover:bg-primary/5">
                                <Plus size={15} /> Add more items
                            </Link>
                        )}
                    </BkCard>

                    {/* Multi-shop checkout only (renders nothing otherwise): what each shop's order comes to. */}
                    <ShopBreakdown cart={cart} />

                    <BkCouponSection
                        // A coupon saved on the cart earlier (another visit, the Store Offers hand-off) is priced by the
                        // server's bill and applied by checkout itself (effective_cart_coupon) — show what the bill applies.
                        appliedCode={coupon?.code || (totals.coupon_code && Number(totals.coupon_discount) > 0 ? totals.coupon_code : null)}
                        saved={Number(totals.coupon_discount || 0)}
                        onOpenPicker={openCouponPicker}
                        onRemove={removeCoupon}
                        code={code} setCode={setCode} onApply={apply} applyBusy={busy}
                    >
                        <CoinsRedeemBlock
                            coinsBalance={coinsBalance} coinsConfig={coinsConfig} coinsToRedeem={coinsToRedeem} setCoinsToRedeem={setCoinsToRedeem}
                            coinMax={coinRedeemCap(coinsConfig, coinsBalance, totals)} coinMin={coinRedeemMin(coinsConfig)}
                            coinsDisc={Number(totals.coins_discount || 0)} subtotalForCap={coinCapSubtotal(totals)}
                        />
                    </BkCouponSection>
                </div>

                <div className="space-y-3 mt-3 lg:mt-0 min-w-0 lg:sticky lg:top-20">
                    <BkAddressSection
                        addr={selectedAddr}
                        onChange={() => setShowAddr(true)}
                        onAdd={() => setPicker(true)}
                        warning={outOfZone && (
                            <BkNotice tone="error" testid="cart-out-of-zone-banner" title="This store doesn't deliver to this address"
                                      body={`It's ${totals.delivery_distance_km} km away; the store delivers within ${totals.delivery_zone_radius_km} km. Pick a closer address.`}
                                      action={<button type="button" onClick={() => setShowAddr(true)} data-testid="cart-out-of-zone-change" className="h-8 px-3 rounded-lg bg-destructive text-white text-[12px] font-bold">Choose another address</button>} />
                        )}
                    />

                    {c.lse && <DeliveryOptions lse={c.lse} value={c.delivery} onChange={c.chooseDelivery} compact />}

                    <BkPaymentSection methods={methods} value={payment} onChange={setPayment} walletBalance={walletBalance}
                                      useWallet={useWallet} setUseWallet={setUseWallet} total={grand} />

                    <BkPriceDetails totals={totals} walletApplied={walletApplied} />

                    {lastError && (
                        <BkNotice tone="error" testid="bk-order-error" title="Your order wasn't placed" body={`${lastError}. Your cart is unchanged — nothing was charged.`}
                                  onDismiss={() => setLastError(null)}
                                  action={<button type="button" onClick={placeOrder} disabled={disabled} className="h-8 px-3 rounded-lg bg-destructive text-white text-[12px] font-bold disabled:opacity-50">Try again</button>} />
                    )}
                    {/* Last card of the summary column from lg; a fixed bottom bar below lg (see BkPlaceOrderBar). */}
                    <BkPlaceOrderBar
                        amount={toPay}
                        label={buttonLabel}
                        disabled={disabled}
                        busy={placing}
                        busyLabel={payPhase === "paying" ? "Waiting for payment…" : payPhase === "confirming" ? "Confirming payment…" : "Placing order…"}
                        onClick={placeOrder}
                        hint={selectedAddr && !outOfZone ? `Delivering to ${selectedAddr.label || "your address"} · ${[selectedAddr.line1, selectedAddr.city].filter(Boolean).join(", ")}` : null}
                    />
                </div>
            </div>

            <BkPaymentOverlay phase={payPhase} />

            {showCouponPicker && (
                <CouponPickerModal busy={busy} available={availCoupons} loading={couponPickerBusy} onApply={applyCouponFromPicker} onClose={() => setShowCouponPicker(false)} />
            )}
            {showAddr && (
                <AddressPickerDrawer addrs={addrs} selectedAddrId={selectedAddrId}
                                     onPick={(id) => { setSelectedAddrId(id); setShowAddr(false); }}
                                     onAddNew={() => { setShowAddr(false); setPicker(true); }}
                                     onClose={() => setShowAddr(false)} />
            )}
            {picker && (
                <AddressPickerModal onClose={() => setPicker(false)} onSaved={async (saved) => {
                    setPicker(false);
                    await loadAddrs();
                    if (saved?.id) setSelectedAddrId(saved.id);
                }} />
            )}
        </div>
    );
}
